import DragIcon from '@assets/icons/drag.svg';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createStyles } from '@/contexts/theme-context';
import {
  DEFAULT_LIST_ITEM_HORIZONTAL_PADDING,
  DRAG_HANDLE_MARGIN,
  DRAG_HANDLE_WIDTH,
  Props,
} from './sortable-list-types';
import {
  DragDropContext,
  Draggable,
  DraggableChildrenFn,
  DraggingStyle,
  Droppable,
} from '@hello-pangea/dnd';
import { ListItemSeparator, ListOption } from './list-item';
import { shadow } from '@/styles';
import { View } from 'react-native';

export const SortableList = <T extends ListOption>({
  disabled = false,
  dragHandleSide = 'right',
  isDragHidden = false,
  itemHeight,
  items,
  ListFooterComponent,
  ListHeaderComponent,
  listItemHorizontalPadding = DEFAULT_LIST_ITEM_HORIZONTAL_PADDING,
  listItemStyle,
  onChange,
  renderInPortal = true,
  renderItemContent,
}: Props<T>) => {
  const [styles, theme] = useStyles();
  const renderDraggable = useRenderDraggable(renderInPortal);
  return (
    <DragDropContext
      onDragEnd={(result) => {
        if (
          !result.destination ||
          result.destination.index === result.source.index
        ) {
          return;
        }

        const updatedItems = [...items];
        const [removed] = updatedItems.splice(result.source.index, 1);
        updatedItems.splice(result.destination.index, 0, removed);

        onChange(updatedItems);
      }}
    >
      {ListHeaderComponent ? <ListHeaderComponent /> : null}
      <Droppable droppableId="list" isDropDisabled={disabled}>
        {(providedDrop) => (
          // react-beautiful-dnd requires a div
          <div
            ref={providedDrop.innerRef}
            style={isDragHidden ? undefined : listStyle}
            {...providedDrop.droppableProps}
          >
            {items.map((item, index) => {
              const viewHeightStyle =
                typeof itemHeight === 'number' ? { height: itemHeight } : null;
              const contentStyle = {
                paddingLeft:
                  dragHandleSide === 'left'
                    ? isDragHidden
                      ? listItemHorizontalPadding
                      : 0
                    : listItemHorizontalPadding,
                paddingRight: isDragHidden
                  ? listItemHorizontalPadding
                  : dragHandleSide === 'left'
                    ? listItemHorizontalPadding
                    : 0,
              };

              const dragHandleStyle = {
                display: 'flex',
                paddingBottom: 4,
                paddingLeft:
                  dragHandleSide === 'left'
                    ? listItemHorizontalPadding
                    : DRAG_HANDLE_MARGIN,
                paddingRight:
                  dragHandleSide === 'right'
                    ? listItemHorizontalPadding
                    : DRAG_HANDLE_MARGIN,
                paddingTop: 4,
              };

              return (
                <Draggable
                  draggableId={item.value as string}
                  index={index}
                  isDragDisabled={disabled}
                  key={item.value}
                >
                  {renderDraggable((providedDrag, snapshot) => {
                    const itemContent = (
                      <View style={[styles.listItemContent, contentStyle]}>
                        {renderItemContent(item, index)}
                      </View>
                    );
                    const dragHandle = isDragHidden ? null : (
                      <View
                        style={[
                          styles.listItemDragButton,
                          {
                            width:
                              DRAG_HANDLE_WIDTH +
                              DRAG_HANDLE_MARGIN +
                              listItemHorizontalPadding,
                          },
                          disabled ? styles.listItemDragButtonDisabled : null,
                        ]}
                      >
                        <div
                          {...providedDrag.dragHandleProps}
                          data-testid="drag-button"
                          style={dragHandleStyle}
                        >
                          <DragIcon fill={theme.colors.inactiveFill} />
                        </div>
                      </View>
                    );
                    return (
                      // react-beautiful-dnd requires a div
                      <div
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                      >
                        <View
                          style={[
                            viewHeightStyle,
                            styles.listItem,
                            index === items.length - 1
                              ? styles.lastListItem
                              : null,
                            snapshot.isDragging ? styles.listItemActive : null,
                            listItemStyle,
                          ]}
                        >
                          {dragHandleSide === 'left' ? (
                            <>
                              {dragHandle}
                              {itemContent}
                            </>
                          ) : (
                            <>
                              {itemContent}
                              {dragHandle}
                            </>
                          )}
                        </View>
                        {index !== items.length - 1 ? (
                          <ListItemSeparator />
                        ) : null}
                      </div>
                    );
                  })}
                </Draggable>
              );
            })}
            {providedDrop.placeholder}
            {ListFooterComponent ? <ListFooterComponent /> : null}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

// Fix draggable offset due to parent layout positioning
// https://github.com/atlassian/react-beautiful-dnd/issues/128#issuecomment-1010053365
export const useRenderDraggable = (renderInPortal: boolean) => {
  const elementRef = useRef<HTMLDivElement>(document.createElement('div'));

  useEffect(() => {
    const element = elementRef.current;
    if (element) {
      element.style.pointerEvents = 'none';
      element.style.position = 'absolute';
      element.style.height = '100%';
      element.style.width = '100%';
      element.style.top = '0';
      document.body.appendChild(element);

      return () => {
        document.body.removeChild(element);
      };
    }
  }, []);

  return (render: DraggableChildrenFn) =>
    (...args: Parameters<DraggableChildrenFn>) => {
      const result = render(...args);
      const style = args[0].draggableProps.style as DraggingStyle;
      return renderInPortal && style.position === 'fixed' ? (
        <>{createPortal(result, elementRef.current)}</>
      ) : (
        result
      );
    };
};

const listStyle = { flex: 1, overflow: 'auto' };

const useStyles = createStyles((theme) => ({
  lastListItem: {
    borderBottomColor: theme.colors.transparent,
  },
  listItem: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  listItemActive: {
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.15,
      radius: 4,
      x: 0,
      y: 0,
    }),
    borderBottomColor: theme.colors.transparent,
  },
  listItemContent: {
    flex: 1,
    userSelect: 'none',
  },
  listItemDragButton: {
    alignItems: 'center',
  },
  listItemDragButtonDisabled: {
    opacity: 0,
  },
}));

export const KeyboardAwareSortableList = SortableList;
